import { compareByAmountFactory } from '@myre/common-primitives';
import {
  ActionButtonComponent,
  BannerComponent,
  ButtonComponent,
  ButtonHeight,
  ButtonSubTheme,
  ButtonTheme,
  ClickOrLinkComponent,
  DocViewerComponent,
  LinkComponent,
  ScrollControlPanelComponent,
  SeparatorComponent,
  TagComponent,
  ToggleButtonComponent,
  ToggleDirective,
  ToggleOnComponent,
  WithToggleDirective,
} from '@myre/ui-primitive-components';
import {
  FixedOnScrollDirective,
  TranslatePipe,
  UnsubscribeOnDestroy,
} from '@myre/ui-primitive-core';
import { EllipsisDirective } from '@myre/ui-primitive-ellipsis';
import {
  MyreIconTooltipComponent,
  TooltipComponent,
} from '@myre/ui-primitive-tooltip';
import { TooltipDirective } from '@myre/ui-primitive-tooltip-directive';

import {
  AsyncPipe,
  NgClass,
  NgPlural,
  NgPluralCase,
  NgTemplateOutlet,
} from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  forwardRef,
  inject,
  Input,
  isDevMode,
  ViewChild,
  ViewChildren,
  ViewContainerRef,
  type AfterViewChecked,
  type AfterViewInit,
  type OnDestroy,
  type OnInit,
  type PipeTransform,
  type QueryList,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  type AbstractControl,
  type UntypedFormGroup,
  type ValidationErrors,
} from '@angular/forms';
import { RouterLink } from '@angular/router';
import { isNil } from 'lodash-es';
import {
  EMPTY,
  from,
  of,
  Subject,
  type Observable,
  type Subscription,
} from 'rxjs';
import { catchError, debounceTime, map, switchMap } from 'rxjs/operators';

import { CalendarPickerComponent } from './components/calendar-picker/calendar-picker.component';
import { CheckboxComponent } from './components/checkbox/checkbox.component';
import { ColorPickerComponent } from './components/color-picker/color-picker.component';
import { DatePickerComponent } from './components/date-picker/date-picker.component';
import { DropdownComponent } from './components/dropdown/dropdown.component';
import { DropdownTemplate } from './components/dropdown/dropdown.directive';
import { DropdownSearchType } from './components/dropdown/types/search.type';
import {
  GooglemapComponent,
  MyreGmTooltipTemplateDirective,
} from './components/googlemap/googlemap.component';
import { MonthPickerComponent } from './components/month-picker/month-picker.component';
import { MultiSelectComponent } from './components/multi-select/multi-select.component';
import { MyuiPeriodPicker } from './components/period-picker';
import { RadioGroupComponent } from './components/radio-group/radio-group.component';
import { SliderComponent } from './components/slider/slider.component';
import { SwitchComponent } from './components/switch/switch.component';
import { TextareaComponent } from './components/textarea/textarea.component';
import { TextboxComponent } from './components/textbox/textbox.component';
import { UploadComponent } from './components/upload/upload.component';
import { WysiwygComponent } from './components/wysiwyg/wysiwyg.component';
import { YearPickerComponent } from './components/year-picker/year-picker.component';
import {
  ElementBanner,
  ElementButton,
  ElementDocViewer,
  ElementGooglemap,
  ElementLink,
  ElementSeparator,
  ElementTemplate,
  ElementText,
  type ElementBase,
  type ElementComponent,
} from './elements';
import { FORM_ITEM_HEIGHT } from './form.constants';
import { MultilevelToggleComponent } from './multilevel-toggle/multilevel-toggle.component';
import { QuestionAlternativeInput } from './questions/question-alternative-input';
import { QuestionBase } from './questions/question-base';
import { QuestionCalendar } from './questions/question-calendar';
import { QuestionCheckbox } from './questions/question-checkbox';
import { QuestionColor } from './questions/question-color';
import { QuestionContainer } from './questions/question-container';
import { QuestionControlService } from './questions/question-control.service';
import { QuestionDate } from './questions/question-date';
import { QuestionDropdown } from './questions/question-dropdown';
import { QuestionFinancialValue } from './questions/question-financial-value';
import { QuestionGroup } from './questions/question-group';
import { QuestionHidden } from './questions/question-hidden';
import { type ISelectItem } from './questions/question-multi-base';
import { QuestionMultiSelect } from './questions/question-multi-select';
import { QuestionMultilevelToggle } from './questions/question-multilevel-toggle';
import { QuestionPeriodPicker } from './questions/question-period-picker';
import { QuestionRadioGroup } from './questions/question-radio-group';
import { QuestionSlider } from './questions/question-slider';
import { QuestionSwitch } from './questions/question-switch';
import { QuestionTextbox } from './questions/question-textbox';
import { QuestionUpload } from './questions/question-upload';
import { QuestionWysiwyg } from './questions/question-wysiwyg';
import { isQuestionGroup } from './questions/question.helpers';

export interface ISubmitParams {
  label: string;
}

export class BaseFormComponent {
  protected readonly _asQuestion = (q: ElementBase): QuestionBase<any> =>
    q as QuestionBase<any>;

  protected readonly _asTextbox = (q: QuestionBase<any>): QuestionTextbox =>
    q as QuestionTextbox;

  protected readonly _asDropdown = (q: QuestionBase<any>): QuestionDropdown =>
    q as QuestionDropdown;

  protected readonly _asPeriodPicker = (
    q: QuestionBase<any>,
  ): QuestionPeriodPicker => q as QuestionPeriodPicker;

  protected readonly _asDate = (q: QuestionBase<any>): QuestionDate =>
    q as QuestionDate;

  protected readonly _asGroup = (q: QuestionBase<any>): QuestionGroup<any> =>
    q as QuestionGroup<any>;

  protected readonly _asContainer = (q: QuestionBase<any>): QuestionContainer =>
    q as QuestionContainer;

  protected readonly _asUpload = (q: QuestionBase<any>): QuestionUpload =>
    q as QuestionUpload;

  protected readonly _asCalendar = (q: QuestionBase<any>): QuestionCalendar =>
    q as QuestionCalendar;

  protected readonly _asCheckbox = (q: QuestionBase<any>): QuestionCheckbox =>
    q as QuestionCheckbox;

  protected readonly _asRadioGroup = (
    q: QuestionBase<any>,
  ): QuestionRadioGroup => q as QuestionRadioGroup;

  protected readonly _asColor = (q: QuestionBase<any>): QuestionColor =>
    q as QuestionColor;

  protected readonly _asMultiSelect = (
    q: QuestionBase<any>,
  ): QuestionMultiSelect => q as QuestionMultiSelect;

  protected readonly _asSwitch = (q: QuestionBase<any>): QuestionSwitch =>
    q as QuestionSwitch;

  protected readonly _asWysiwyg = (q: QuestionBase<any>): QuestionWysiwyg =>
    q as QuestionWysiwyg;

  protected readonly _asMultilevelToggle = (
    q: QuestionBase<any>,
  ): QuestionMultilevelToggle => q as QuestionMultilevelToggle;

  protected readonly _asSlider = (q: QuestionBase<any>): QuestionSlider =>
    q as QuestionSlider;

  protected readonly _asAlternativeInput = (
    q: QuestionBase<any>,
  ): QuestionAlternativeInput => q as QuestionAlternativeInput;

  protected readonly _asElementTemplate = (q: ElementBase): ElementTemplate =>
    q as ElementTemplate;

  protected readonly _asElementText = (q: ElementBase): ElementText =>
    q as ElementText;

  protected readonly _asElementLink = (q: ElementBase): ElementLink =>
    q as ElementLink;

  protected readonly _asElementButton = (q: ElementBase): ElementButton =>
    q as ElementButton;

  protected readonly _asDocViewer = (q: ElementBase): ElementDocViewer =>
    q as ElementDocViewer;

  protected readonly _asElementBanner = (q: ElementBase): ElementBanner =>
    q as ElementBanner;

  protected readonly _asGoogleMap = (q: ElementBase): ElementGooglemap<any> =>
    q as ElementGooglemap<any>;

  protected readonly _asSeparator = (q: ElementBase): ElementSeparator =>
    q as ElementSeparator;
}

@Component({
  selector: 'myre-form',
  templateUrl: './form.component.html',
  styleUrls: ['./form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ClickOrLinkComponent,
    SeparatorComponent,
    GooglemapComponent,
    MyreGmTooltipTemplateDirective,
    NgTemplateOutlet,
    ActionButtonComponent,
    DocViewerComponent,
    BannerComponent,
    forwardRef(() => FormQuestionComponent),
    FormsModule,
    ReactiveFormsModule,
    TranslatePipe,
  ],
})
export class FormComponent
  extends BaseFormComponent
  implements OnDestroy, AfterViewChecked, AfterViewInit
{
  public changeDetectionRef = inject(ChangeDetectorRef);
  private _questionControlService = inject(QuestionControlService);

  @ViewChildren('formElement')
  public formElements: QueryList<ElementRef>;

  @ViewChildren('component', { read: ViewContainerRef })
  public viewContainerRefs: QueryList<ViewContainerRef>;

  @ViewChild('wrapper')
  public wrapper: ElementRef;

  @Input()
  public set formGroup(formGroup: FormGroup) {
    this._questionControlService.destroy(this._formGroup);

    if (isDevMode() && formGroup && !formGroup.__isMyreForm) {
      // eslint-disable-next-line no-console
      console.warn('Please generate form with QuestionControlService');
    }

    this._formGroup = formGroup;
    this._subscription?.unsubscribe();

    if (this.formGroup) {
      this._subscription = this.formGroup.root.valueChanges.subscribe(() => {
        this._resetErrorMessage();
        this.changeDetectionRef.markForCheck();
      });
    }
  }

  public get formGroup(): FormGroup {
    return this._formGroup;
  }

  @Input()
  public fromGroup = false;

  @Input()
  public set elements(elts: ElementBase[]) {
    elts ||= [];
    this._visibleElements = elts.filter(e => !(e instanceof QuestionHidden));
    this._allElements = elts;

    for (const elt of elts) {
      elt.registerOnChange(() => {
        this.changeDetectionRef.detectChanges();
      });
    }
  }

  public get elements(): ElementBase[] {
    return (this._visibleElements || []).filter(
      e => (e.enabled || e.keepOnDisabled) && e.visible,
    );
  }

  @Input()
  public submitParams?: ISubmitParams;

  @Input()
  public errorMessage: string | null = null;

  @Input()
  public prefix = '';

  @Input()
  public destroyFormGroupOnComponentDestroy = true;

  @UnsubscribeOnDestroy()
  private _subscription: Subscription;

  private _allElements: ElementBase[] = [];
  private _visibleElements: ElementBase[] = [];
  private _formGroup: FormGroup;

  private _viewContainerRefComponentMap = new Map<ViewContainerRef, any>();

  public trackBy(_: number, element: ElementBase): number | string {
    return element.key;
  }

  public ngAfterViewInit(): void {
    this._handleViewContainerRefs(this.viewContainerRefs.toArray());

    this.viewContainerRefs.changes.subscribe(viewContainerRefs => {
      this._handleViewContainerRefs(viewContainerRefs.toArray());
    });
  }

  public ngAfterViewChecked(): void {
    this._handleLabels();

    this._handleViewContainerRefs(this.viewContainerRefs.toArray());
  }

  public ngOnDestroy(): void {
    if (this.destroyFormGroupOnComponentDestroy) {
      this._questionControlService.destroy(this.formGroup);
    }
  }

  public get allElements(): ElementBase[] {
    return this._allElements || [];
  }

  public isFirstLine(element: ElementBase): boolean {
    return this.getLine(element) === 1;
  }

  public getLine(element: ElementBase): number {
    let line = 1;
    let size = 0;

    for (const elt of this.elements) {
      size += elt.size;

      if (size > 100) {
        line++;
        size = elt.size;
      } else if (size === 100) {
        size = 0;

        if (element !== elt) {
          line++;
        }
      }

      if (element === elt) {
        return line;
      }
    }
  }

  public isQuestion(element: ElementBase): boolean {
    return element instanceof QuestionBase;
  }

  /**
   * A dummy element occupies space horizontally to keep lines organization but has nothing to display vertically
   *
   * We mark element as dummy in three cases:
   * - if element is disabled
   * - if element is QuestionGroup which has neither elements nor customActionAddItem nor initialItemValue
   * - if element is ElementText without text
   *
   * For second case above we need these three conditions, otherwise we have two cases when display the add button:
   * - initialItemValue is defined and we don't have a customActionAddItem
   * - customActionAddItem is defined and we don't have elements
   */
  public isDummy(element: ElementBase): boolean {
    return (
      element.disabled ||
      (element instanceof QuestionGroup &&
        element.elements.length === 0 &&
        !element.customActionAddItem &&
        !element.initialItemValue) ||
      (element instanceof ElementText && !element.text)
    );
  }

  public hasUpperLabel(element: ElementBase): boolean {
    return this._questionControlService.hasUpperLabel(element);
  }

  private _handleLabels(): void {
    const elts = this.formElements.toArray();

    if (!elts.length) {
      return;
    }

    const lines = +elts[elts.length - 1].nativeElement.getAttribute('line');
    let firstNonEmptyLine = 0;

    for (let i = 1; i <= lines; i++) {
      const allElementsOfLine = elts.filter(e => {
        return +e.nativeElement.getAttribute('line') === i;
      });

      for (const presentElementOfLine of allElementsOfLine) {
        presentElementOfLine.nativeElement.style.marginTop = '0px';
      }

      const allVisibleElementsOfLine = allElementsOfLine.filter(e => {
        return !e.nativeElement.hasAttribute('dummy');
      });

      if (allVisibleElementsOfLine.length) {
        firstNonEmptyLine ||= i;
      }

      const labelLessOfLine = allVisibleElementsOfLine.filter(e =>
        e.nativeElement.hasAttribute('label-less'),
      );

      const allVisibleElementsOfLineWithLabel = allVisibleElementsOfLine.filter(
        e => !labelLessOfLine.includes(e),
      );

      const labelMap = new Map<Element, Element>();

      const labelHeight = allVisibleElementsOfLineWithLabel.reduce((v, e) => {
        const label = e.nativeElement.querySelector('.upper-labels');

        if (!label) {
          return v;
        }

        labelMap.set(e.nativeElement, label);

        return Math.max(v, label.clientHeight);
      }, 0);

      for (const visibleElement of allVisibleElementsOfLineWithLabel) {
        const label = labelMap.get(visibleElement.nativeElement);

        if (label?.clientHeight !== labelHeight) {
          visibleElement.nativeElement.style.marginTop =
            labelHeight - label.clientHeight + 'px';
        }
      }

      if (!labelLessOfLine.length) {
        continue;
      }

      if (labelLessOfLine.length !== allVisibleElementsOfLine.length) {
        for (const labelLessOfLineElement of labelLessOfLine) {
          labelLessOfLineElement.nativeElement.style.marginTop =
            labelHeight + 'px';

          const { classList } = labelLessOfLineElement.nativeElement;

          if (
            !classList.contains('group') &&
            !classList.contains('multi') &&
            !classList.contains('container')
          ) {
            labelLessOfLineElement.nativeElement.style.height =
              FORM_ITEM_HEIGHT + 'px';
          }
        }
      } else if (i > firstNonEmptyLine) {
        for (const labelLessOfLineElement of labelLessOfLine) {
          labelLessOfLineElement.nativeElement.style.marginTop = '16px';
        }
      }
    }
  }

  private _resetErrorMessage(): void {
    this.errorMessage = null;
  }

  private _handleViewContainerRefs(viewContainers: ViewContainerRef[]): void {
    if (!viewContainers.length) {
      return;
    }

    const componentElements = this._visibleElements.filter(
      el => el.elementType === 'component',
    );

    for (let i = 0; i < componentElements.length; i++) {
      const componentElement = componentElements[i] as ElementComponent<any>;
      const viewContainer = viewContainers[i];

      if (
        viewContainer &&
        componentElement.component &&
        !this._viewContainerRefComponentMap.has(viewContainer)
      ) {
        const component = viewContainer.createComponent(
          componentElement.component,
        );

        this._viewContainerRefComponentMap.set(viewContainer, component);

        componentElement.created(component.instance);

        this.changeDetectionRef.detectChanges();
      }
    }
  }
}

export interface IDataBinding {
  key: string;
  binding: any;
}

const QUESTION_LIST_TYPES = [
  'textbox',
  'dropdown',
  'date',
  'color',
  'checkbox',
  'template',
  'alternativeInput',
  'text',
  'link',
  'button',
];

@Component({
  selector: 'myre-question',
  templateUrl: './questions/form-question.component.html',
  styleUrls: ['./questions/form-question.component.scss'],
  imports: [
    ActionButtonComponent,
    AsyncPipe,
    ButtonComponent,
    CalendarPickerComponent,
    CheckboxComponent,
    ClickOrLinkComponent,
    ColorPickerComponent,
    DatePickerComponent,
    DropdownComponent,
    DropdownTemplate,
    EllipsisDirective,
    FixedOnScrollDirective,
    FormComponent,
    FormsModule,
    LinkComponent,
    MonthPickerComponent,
    MultiSelectComponent,
    MultilevelToggleComponent,
    MyreIconTooltipComponent,
    NgClass,
    NgPlural,
    NgPluralCase,
    NgTemplateOutlet,
    RadioGroupComponent,
    ReactiveFormsModule,
    RouterLink,
    ScrollControlPanelComponent,
    SeparatorComponent,
    SliderComponent,
    SwitchComponent,
    TagComponent,
    TextareaComponent,
    TextboxComponent,
    ToggleButtonComponent,
    ToggleDirective,
    ToggleOnComponent,
    TooltipComponent,
    TooltipDirective,
    TranslatePipe,
    UploadComponent,
    WithToggleDirective,
    WysiwygComponent,
    YearPickerComponent,
    MyuiPeriodPicker,
  ],
})
export class FormQuestionComponent<T extends QuestionBase<any>>
  extends BaseFormComponent
  implements OnInit
{
  private readonly _changeDetectionRef = inject(ChangeDetectorRef);
  private readonly _questionControlService = inject(QuestionControlService);
  private readonly _destroyRef = inject(DestroyRef);

  private readonly _autocompleteQuery$ = new Subject<string>();

  @Input()
  public question: T;

  @Input()
  public formGroup: UntypedFormGroup;

  @Input()
  public fromGroup: boolean;

  @Input()
  public firstLine: boolean;

  @Input()
  public prefix: string = '';

  @ViewChild('fileUploader')
  public fileUploader: UploadComponent;

  @ViewChildren(ToggleDirective)
  public toggles: QueryList<ToggleDirective>;

  protected readonly isNil = isNil;

  public readonly isQuestionGroup = isQuestionGroup;

  @ViewChild(ScrollControlPanelComponent)
  protected _scrollControlPanelComponent: ScrollControlPanelComponent;

  protected _isHeaderFixed = false;
  protected _isTotalFixed = false;

  protected readonly _ButtonTheme = ButtonTheme;
  protected readonly _ButtonHeight = ButtonHeight;
  protected readonly _ButtonSubTheme = ButtonSubTheme;
  protected readonly _dropdownSearchType = DropdownSearchType;

  public trackBy(index: number): number {
    return index;
  }

  public ngOnInit(): void {
    if (
      this.question.elementType === 'textbox' &&
      this.question.type === 'string'
    ) {
      this.question.type = 'text';
    }

    if (this.question instanceof QuestionUpload) {
      this.question.registerUpload(() => this.fileUploader.upload());
    }

    this._setupAutocomplete();
  }

  /**
   * Drives the `autocomplete` loader through a single per-question stream so a
   * label search can debounce, gate on a minimum query length and — via
   * `switchMap` — drop the result of any request a newer keystroke superseded.
   * A question without a loader (the common case) never builds the stream, so
   * its behaviour is untouched; a loader without a debounce/min-chars keeps
   * firing on every keystroke exactly as before (the stream forwards each
   * query synchronously).
   */
  private _setupAutocomplete(): void {
    if (
      !(this.question instanceof QuestionTextbox) ||
      !this.question.autocomplete
    ) {
      return;
    }

    const debounce = this.question.autocompleteDebounce ?? 0;

    let query$: Observable<string> = this._autocompleteQuery$;

    if (debounce > 0) {
      query$ = query$.pipe(debounceTime(debounce));
    }

    query$
      .pipe(
        switchMap(query => {
          // Read the LIVE question on each search, never a value captured at
          // setup: this component instance can be re-bound to a new question
          // object (the @Input changes with no re-init), and both the loader that
          // runs and the `results` the outcome lands on must belong to the
          // question searched now. The question is carried through the stream so
          // a slow load still applies to the one it was started for.
          const question = this.question;

          if (
            !(question instanceof QuestionTextbox) ||
            !question.autocomplete
          ) {
            return EMPTY;
          }

          const textbox = question;

          if (query.length < (textbox.autocompleteMinChars ?? 0)) {
            return of({ textbox, results: [] as ISelectItem[] });
          }

          return from(textbox.autocomplete(query)).pipe(
            map(results => ({ textbox, results })),
            catchError(() => of({ textbox, results: [] as ISelectItem[] })),
          );
        }),
        takeUntilDestroyed(this._destroyRef),
      )
      .subscribe(({ textbox, results }) => {
        textbox.results = results;
        this._changeDetectionRef.markForCheck();
      });
  }

  public get control(): AbstractControl | null {
    return this.formGroup ? this.formGroup.controls[this.question.key] : null;
  }

  public canSave(form: AbstractControl): boolean {
    return form.valid && form.dirty;
  }

  public hasError(question: QuestionBase<any> = this.question): boolean {
    return (
      !question.valid &&
      question.getFormControl().enabled &&
      question.getFormControl().touched
    );
  }

  public hasWarning(question: QuestionBase<any> = this.question): boolean {
    return (
      !this.hasError(question) &&
      !!question.getFormControl().warnings &&
      question.getFormControl().touched
    );
  }

  public hasInfo(question: QuestionBase<any> = this.question): boolean {
    return (
      !this.hasError(question) &&
      !!question.getFormControl().info &&
      question.getFormControl().touched
    );
  }

  public getMessagesByType(
    type: 'errors' | 'warnings' | 'info',
    control: AbstractControl,
  ): IDataBinding[] {
    return this._getControlData(type, control);
  }

  public search({ query }: { query: string }): void {
    if (
      this.question instanceof QuestionTextbox &&
      this.question.autocomplete
    ) {
      this._autocompleteQuery$.next(query);
    }
  }

  public getQuestionsForList(elements: ElementBase[]): QuestionBase<any>[] {
    const filteredElements = elements.filter(
      e => e.visible && QUESTION_LIST_TYPES.includes(e.elementType),
    ) as QuestionBase<any>[];

    if (elements.some(el => typeof el.order !== 'number')) {
      return filteredElements;
    }

    return filteredElements.sort(
      compareByAmountFactory(element => element.order),
    );
  }

  public hasLabel(elements: QuestionBase<any>[]): boolean {
    return elements.some(e => !!e.label?.trim().length);
  }

  public hasUpperLabel(element: QuestionBase<any>): boolean {
    return this._questionControlService.hasUpperLabel(element);
  }

  public hasMarginTop(element: QuestionBase<any>, index: number): boolean {
    if (index === 0) {
      return this.firstLine || !element.label;
    }

    if (isQuestionGroup(element)) {
      return !(element.showIndex && !element.getLabel(index));
    }

    return false;
  }

  public trackByError(_: number, error: IDataBinding): string {
    return error.key;
  }

  protected _addNewItem(question: QuestionGroup<any>): void {
    this._scrollControlPanelComponent?.resetScroll();
    question.addNewItem();
  }

  private _getControlData(
    type: 'errors' | 'warnings' | 'info',
    control: AbstractControl,
  ): IDataBinding[] {
    if (!control) {
      return [];
    }

    const result: ValidationErrors = control[type] ?? {};

    return Object.entries(result).map(([key, binding]) => ({
      binding,
      key: key === 'required' ? '_system.errorRequiredField' : key,
    }));
  }

  protected _getDisplayOnly(q: QuestionBase<any>): boolean {
    if (q instanceof QuestionFinancialValue) {
      return q.displayOnly;
    }
  }

  protected _getDisplayOnlyNullValue(q: QuestionBase<any>): string {
    if (q instanceof QuestionFinancialValue) {
      return q.displayOnlyNullValue;
    }
  }

  protected _getPipe(q: QuestionBase<any>): PipeTransform {
    if (q instanceof QuestionFinancialValue) {
      return q.getPipe() || q.pipe;
    }
  }

  protected _isSearchableDropdown(question: QuestionDropdown): boolean {
    return (
      question.elementType === 'dropdown' && question.isSearchable === true
    );
  }

  protected _getDropdownSearchType(
    question: QuestionDropdown,
  ): DropdownSearchType {
    return this._isSearchableDropdown(question)
      ? DropdownSearchType.Input
      : DropdownSearchType.Soft;
  }
}
